import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { media } from 'context';
import { HEADER_Z_INDEX } from 'ds';
import { BROKERS_PATH, GFP_PATH, SEARCH_PATH } from 'routes';
import { BLUE_25_HEX } from 'shared';
import { selectPublicHeaderHasShadow } from 'store/UI/selectors';
import { selectUser } from 'store/User/selectors';

import Navigation from './Navigation';
import NavigationMobile from './Navigation/Mobile';
import { PATHS_WITH_BLUE_25_BACKGROUND } from './Navigation/constants';
import StickyHeader from './StickyHeader';
import { PUBLIC_NAV_HEIGHT } from './constants';

interface Props {
  hideLinks?: boolean;
  showCta?: boolean;
}

const PATHS_WITH_TRANSPARENT_HEADER: { value: string; mobileOnly: boolean }[] = [
  { value: BROKERS_PATH, mobileOnly: false },
  { value: GFP_PATH, mobileOnly: true }
];

const Header: React.FC<Props> = ({ showCta }) => {
  const { pathname } = useLocation();
  const { isMobile } = useContext(media);
  const user = useSelector(selectUser);
  const reduxHasShadow = useSelector(selectPublicHeaderHasShadow);
  const [isTransparentMode, setIsTransparentMode] = useState<boolean>(
    PATHS_WITH_TRANSPARENT_HEADER.some(path => path.value === pathname)
  );
  const [isPublicNavOpen, setIsPublicNavOpen] = useState<boolean>(false);
  const [isAccountNavOpen, setIsAccountNavOpen] = useState<boolean>(false);
  const [opacity, setOpacity] = useState<number>();
  const [backgroundColor, setBackgroundColor] = useState<string>(BLUE_25_HEX); // blue-25 for non-transparent paths
  const [openMenuIndex, setOpenMenuIndex] = useState<number>(-1);
  const pathWithTransparentHeader = PATHS_WITH_TRANSPARENT_HEADER.some(path => path.value === pathname);

  useEffect(() => {
    if (PATHS_WITH_TRANSPARENT_HEADER.some(path => path.value === pathname && (path.mobileOnly ? isMobile : true))) {
      setIsTransparentMode(true);
    } else {
      setOpenMenuIndex(-1);
      setOpacity(1);
      setBackgroundColor(PATHS_WITH_BLUE_25_BACKGROUND.includes(pathname as any) ? BLUE_25_HEX : '#FFFFFF');
      setIsTransparentMode(false);
      setIsAccountNavOpen(false);
      setIsAccountNavOpen(false);
    }
  }, [pathname, isMobile]);

  const { lg } = useContext(media);

  const isSearchPage = pathname === SEARCH_PATH;
  const isWorkspacesOrSearchPage = ['workspaces'].includes(pathname.split('/')[1]) || isSearchPage;
  const menuIsOpen = isPublicNavOpen || isAccountNavOpen || openMenuIndex !== -1;

  const hasShadow = isTransparentMode ? false : isWorkspacesOrSearchPage ? false : reduxHasShadow;

  const handleScrollY = (scrollY: number) => {
    if (scrollY > PUBLIC_NAV_HEIGHT) {
      if (isTransparentMode) {
        setIsTransparentMode(false);
        setOpacity(1);
      }
      setBackgroundColor('#FFFFFF'); // white when scrolled past threshold
    } else {
      if (scrollY === 0 && isTransparentMode) {
        setOpacity(0);
      } else {
        const pct_scrolled = scrollY / PUBLIC_NAV_HEIGHT;

        if (isTransparentMode) {
          setOpacity(0.85 * pct_scrolled + 0.15);
        }

        // For non-transparent paths, transition from blue-25 to white
        if (
          !isTransparentMode &&
          !pathWithTransparentHeader &&
          PATHS_WITH_BLUE_25_BACKGROUND.includes(pathname as any)
        ) {
          const blue25 = { r: 248, g: 251, b: 255 }; // #F8FBFF
          const white = { r: 255, g: 255, b: 255 }; // #FFFFFF

          const r = Math.round(blue25.r + (white.r - blue25.r) * pct_scrolled);
          const g = Math.round(blue25.g + (white.g - blue25.g) * pct_scrolled);
          const b = Math.round(blue25.b + (white.b - blue25.b) * pct_scrolled);

          setBackgroundColor(`rgb(${r}, ${g}, ${b})`);
        }
      }
    }
  };

  return (
    <StickyHeader
      top={0}
      zIndex={HEADER_Z_INDEX}
      shadow={hasShadow}
      backgroundColor={isTransparentMode ? 'transparent' : undefined}
      background={isTransparentMode ? `rgba(255, 255, 255, ${menuIsOpen ? 1 : opacity})` : backgroundColor}
      transition="background 200ms"
      onMouseEnter={isTransparentMode ? () => setOpacity(1) : undefined}
      onMouseLeave={
        isTransparentMode
          ? () => {
              handleScrollY(window.scrollY);
            }
          : undefined
      }
      onScroll={scrollY => handleScrollY(scrollY)}
    >
      {lg ? (
        <Navigation
          showCta={showCta}
          isSearchPage={isSearchPage}
          isTransparentMode={isTransparentMode}
          textOpacity={menuIsOpen ? 1 : opacity}
          backgroundColor={isTransparentMode ? undefined : backgroundColor}
          user={user}
          openMenuIndex={openMenuIndex}
          setOpenMenuIndex={setOpenMenuIndex}
        />
      ) : (
        <NavigationMobile
          showCta={showCta}
          isSearchPage={isSearchPage}
          isTransparentMode={isTransparentMode}
          setIsAccountNavOpen={setIsAccountNavOpen}
          isAccountNavOpen={isAccountNavOpen}
          setIsPublicNavOpen={setIsPublicNavOpen}
          isPublicNavOpen={isPublicNavOpen}
          textOpacity={opacity}
          user={user}
        />
      )}
    </StickyHeader>
  );
};

export default Header;
